import type { CuentaSedeRow } from '../../lib/api.js';
import { fmtInt } from '../../lib/format.js';

/**
 * Matriz cuenta × sede: dónde produce cada cuenta.
 *
 * Es una tabla, no un gráfico: los valores exactos importan (son las noches que
 * se negocian con el cliente) y además hace de vista tabular accesible del
 * ranking. La magnitud se refuerza con un tinte secuencial de UN solo tono
 * relativo al máximo de la tabla; el número siempre va en tinta de texto, así
 * que se lee aunque el tinte no se distinga.
 */
export function CuentasSedeMatrix({
  rows,
  sedes,
}: {
  rows: CuentaSedeRow[];
  /** Códigos de sede a mostrar como columnas, en orden. */
  sedes: string[];
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">Sin producción de cuentas para el período.</p>;
  }
  const max = Math.max(...rows.flatMap((r) => sedes.map((s) => r.porSede[s] ?? 0)), 1);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-700 text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-3 text-left font-medium">Cuenta</th>
            {sedes.map((s) => (
              <th key={s} className="px-2 py-2 text-right font-medium">
                {s}
              </th>
            ))}
            <th className="py-2 pl-3 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cuenta} className="border-b border-slate-800/80">
              <td className="py-1.5 pr-3 text-slate-200">{r.cuenta}</td>
              {sedes.map((s) => {
                const v = r.porSede[s] ?? 0;
                return (
                  <td
                    key={s}
                    className="px-2 py-1.5 text-right tabular-nums text-slate-200"
                    style={
                      v > 0
                        ? {
                            backgroundColor: `rgba(57, 135, 229, ${(0.12 + 0.5 * (v / max)).toFixed(3)})`,
                          }
                        : undefined
                    }
                  >
                    {v === 0 ? <span className="text-slate-600">0</span> : fmtInt(v)}
                  </td>
                );
              })}
              <td className="py-1.5 pl-3 text-right font-medium tabular-nums text-slate-100">
                {fmtInt(r.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
